<template>
  <v-card flat class="border-0 !bg-[rgb(var(--v-theme-surface))]">
    <v-card-title class="d-flex justify-space-between align-center">
      <span>Step 4: Review Third-Party Liens</span>
      <a
        class="text-primary text-body-2 font-weight-medium text-decoration-none"
        data-cy="edit-on-liens-tab"
        href="#"
        @click.prevent="goToLiensTab"
      >
        {{ redesignEnabled ? 'Manage liens on the Liens tab →' : 'Go to liens tab →' }}
      </a>
    </v-card-title>
    <v-card-text>
      <!-- Loading skeleton -->
      <SkeletonRenderer
        v-if="uiState.loading"
        :spec="[
          { type: 'list-item' },
          { type: 'list-item' },
          { type: 'list-item' },
          { type: 'list-item' },
          { type: 'list-item' },
        ]"
        container-class="mb-4"
      />

      <template v-else>
        <!-- Empty state: no liens at all -->
        <div v-if="!liensData || !liensData.lienGroups?.some(g => g.liens.length > 0)" class="text-center py-10 text-grey-darken-1">
          <div class="font-weight-bold mb-1">No liens recorded</div>
          <div class="text-body-2 text-grey-darken-1">
            Add liens on the Liens tab to include them in the settlement
            statement.
          </div>
        </div>

        <!-- Lien groups -->
        <template v-else>
          <!-- Non-blocking warning: finalized liens not yet pushed to QBO -->
          <AlertBar
            v-if="showUnpushedWarning"
            type="warning"
            variant="tonal"
            class="mb-4"
            data-cy="unpushed-liens-warning"
          >
            <div class="d-flex justify-space-between align-center flex-wrap gap-2">
              <span>
                {{ pluralizeWithCount(unpushedFinalizedCount, 'finalized lien') }}
                not yet submitted to QuickBooks. Submit from the Liens tab before
                finalizing the settlement to ensure trust checks are created in QuickBooks.
              </span>
              <LlSecondaryButton
                variant="outlined"
                color="warning"
                size="small"
                data-cy="go-to-liens-tab-warning"
                @click="goToLiensTab"
              >
                Go to Liens Tab &rarr;
              </LlSecondaryButton>
            </div>
          </AlertBar>

          <AlertBar
            type="info"
            class="mb-4"
          >
            <span class="font-weight-bold">Liens are loaded from the Liens tab.</span>
            All liens are included in the settlement statement.
          </AlertBar>

          <div
            v-for="group in liensData.lienGroups"
            :key="group.lienType ?? 'unknown'"
            v-show="group.liens.length > 0"
            class="bg-white border rounded-lg mb-3 overflow-hidden"
          >
            <div class="bg-grey-lighten-5 px-5 py-2 text-caption font-weight-bold text-grey-darken-2 text-uppercase tracking-wide border-b">
              {{ group.displayName }} Liens
            </div>

            <div
              v-for="(lien, idx) in group.liens"
              :key="lien.lienId"
              class="flex justify-between items-center px-5 py-2 text-body-2 cursor-pointer hover:bg-grey-lighten-5 transition-colors"
              :class="idx < group.liens.length - 1 ? 'border-b border-grey-lighten-4' : ''"
              data-cy="lien-row"
              @click="openEditPanel(lien)"
            >
              <div class="flex-1 min-w-0">
                <div class="font-weight-medium text-grey-darken-4">{{ lien.payeeName }}</div>
                <div v-if="lien.description" class="text-caption text-grey mt-0.5">
                  {{ lien.description }}
                </div>
              </div>
              <div class="flex items-center gap-4">
                <LlTooltip :text="isPushed(lien.status) ? pushedStatusTooltip : undefined">
                  <template #activator>
                <div @click.stop>
                <v-menu :disabled="isPushed(lien.status)">
                  <template #activator="{ props: statusMenuProps }">
                    <v-chip
                      v-bind="statusMenuProps"
                      size="small"
                      rounded="pill"
                      :disabled="isPushed(lien.status)"
                      class="cursor-pointer lien-status-badge font-weight-medium"
                      :class="`lien-status--${lien.status.toLowerCase()}`"
                      data-cy="lien-status-chip"
                    >
                      {{ LIEN_STATUS_LABELS[lien.status.toLowerCase()] ?? lien.status }}
                      <v-icon size="12" class="lien-status-caret">mdi-chevron-down</v-icon>
                    </v-chip>
                  </template>
                  <v-list density="compact" class="!rounded-lg !shadow-xl min-w-[160px] !py-1">
                    <v-list-item
                      v-for="opt in statusOptions"
                      :key="opt.value"
                      :class="['transition-colors', lien.status === opt.value ? 'font-semibold' : 'text-gray-700']"
                      :style="lien.status === opt.value ? STATUS_STYLES[opt.value] : {}"
                      @click="updateLienStatus(lien, opt.value)"
                    >
                      <template #prepend>
                        <span
                          class="w-2 h-2 rounded-full flex-shrink-0 mr-2"
                          :style="{ backgroundColor: STATUS_DOT_COLORS[opt.value] ?? '#94a3b8' }"
                        />
                      </template>
                      <v-list-item-title class="text-sm">{{ opt.label }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                </div>
                  </template>
                </LlTooltip>
                <span
                  class="text-right min-w-[96px]"
                  style="font-variant-numeric: tabular-nums"
                  :class="redesignEnabled ? amountEmphasisClass : 'font-weight-medium'"
                >
                  {{ formatCurrency(lien.amount) }}
                </span>
              </div>
            </div>

            <!-- Subtotals are "totals", not per-lien amounts, so they stay bold in
                 both designs — the confirm-state emphasis (AC-7) applies only to the
                 individual lien-row amounts above. -->
            <div class="flex justify-between px-5 py-2 bg-grey-lighten-5 font-weight-bold text-body-2 border-t">
              <span>{{ group.displayName }} Subtotal</span>
              <span>{{ formatCurrency(group.subtotal) }}</span>
            </div>
          </div>

          <!-- Total Liens — redesign: matches the totals-bar style used for deposits
               (Step 1) and expenses (Step 2). Legacy: unchanged boxed total. -->
          <div
            v-if="!redesignEnabled"
            class="bg-white border-2 border-grey-darken-4 rounded-lg pa-4 flex justify-between text-body-1 font-weight-bold mt-4"
            data-cy="lien-grand-total"
          >
            <span>TOTAL LIENS</span>
            <span>{{ formatCurrency(grandTotal) }}</span>
          </div>
          <!-- Redesign Total Liens: mirrors the Step 1/2 totals bar exactly —
               bold black label + bold green amount (gray when zero). NOT the
               italic/gray confirm-state emphasis, which applies only to the
               per-lien row amounts (AC-4 vs AC-7). -->
          <div
            v-else
            class="d-flex justify-space-between align-center px-3 py-3 mt-4"
            style="border-top: 2px solid #e0e0e0;"
            data-cy="lien-grand-total"
          >
            <span class="text-body-1 font-weight-bold">
              Total Liens
            </span>
            <span
              class="font-weight-bold"
              style="font-size: 16px;"
              :class="grandTotal > 0 ? 'text-green-darken-2' : 'text-grey-lighten-1'"
            >
              {{ formatCurrency(grandTotal) }}
            </span>
          </div>
        </template>

        <!-- Navigation -->
        <div class="sett-step-footer">
          <LlSecondaryButton @click="emit('go-back')">
            &larr; Back
          </LlSecondaryButton>

          <LlTooltip
            :text="confirmDisabledReason"
          >
            <template #activator>
              <LlSecondaryButton
                color="primary"
                :disabled="!!liensData?.hasUnfinalizedLiens || uiState.loadError"
                :loading="uiState.saving"
                data-cy="confirm-liens-btn"
                @click="confirmLiens"
              >
                Confirm Liens &rarr;
              </LlSecondaryButton>
            </template>
          </LlTooltip>
        </div>
      </template>
    </v-card-text>
  </v-card>

  <!-- Teleport to body so the panel escapes v-stepper transform stacking context -->
  <Teleport to="body">
    <LienFormPanel
      v-model="showEditPanel"
      :matter-id="matterId"
      :lien="editingLien"
      :loading="panelLoading"
      @saved="onLienSaved"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { useSettlementStore } from '@/modules/matters/settlement/stores/useSettlementStore';
import { useLienStore } from '@/stores/useLienStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { formatCurrency, pluralizeWithCount } from '@/services/utils';
import { LIEN_STATUS_OPTIONS, LIEN_STATUS_LABELS, STATUS_DOT_COLORS, STATUS_STYLES } from '@/constants/lienConstants';
import type { LienData } from '@/models/lienTypes';
import { countUnpushedFinalizedLiens, isPushed } from '@/utils/lienHelpers';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import AlertBar from '@/components/AlertBar.vue';
import SkeletonRenderer from '@/components/SkeletonRenderer.vue';
import LienFormPanel from '@/modules/matters/LienFormPanel.vue';
import type { FromTableLien } from '@/models/settlementTypes';

const props = defineProps({
  settlementId: { type: String, required: true },
  matterId: { type: String, required: true },
  state: { type: Object, required: false, default: undefined },
  /** Gate the SC-45784 redesign UI (bar-style total, confirm-state emphasis) behind this flag.
   *  false = legacy design (boxed "TOTAL LIENS", static bold weight, unchanged).
   *  true  = new design (totals bar matching Step 1/2, italic/gray → bold/black on confirm). */
  redesignEnabled: { type: Boolean, default: false },
});

const emit = defineEmits<{
  (e: 'confirmed'): void;
  (e: 'go-back'): void;
}>();

const settlementStore = useSettlementStore();
const lienStore = useLienStore();
const { handleError, handleSuccess } = useSnackbarHandler();

const statusOptions = LIEN_STATUS_OPTIONS;

const pushedStatusTooltip =
  'This lien has been submitted to QuickBooks. Undo the submission before changing its status.';

const uiState = reactive({
  loading: true,
  saving: false,
  loadError: false,
});

// ── Edit panel state ─────────────────────────────────────
const showEditPanel = ref(false);
const editingLien = ref<LienData | null>(null);
const panelLoading = ref(false);

const liensData = computed(() => settlementStore.fromTableLiens);

const grandTotal = computed(() => liensData.value?.totalLiens ?? 0);

/** Switches lien amounts from italic/gray to bold/black once Step 4 is confirmed. */
const stepConfirmed = computed(() => settlementStore.completedSteps.includes(4));

/** Shared class for every lien amount/label under the redesign — single source of
 *  truth for the confirm-state emphasis rule (avoids repeating the ternary per row). */
const amountEmphasisClass = computed(() =>
  (stepConfirmed.value ? 'font-weight-bold' : 'font-italic text-grey-darken-1'));

// All liens flattened across groups — for QBO push checks.
const allLiens = computed(() =>
  liensData.value?.lienGroups?.flatMap((g) => g.liens) ?? [],
);

const unpushedFinalizedCount = computed(() =>
  countUnpushedFinalizedLiens(allLiens.value),
);

// Non-blocking warning — when connected to QBO and ≥1 finalized lien is unpushed.
const showUnpushedWarning = computed(
  () =>
    (liensData.value?.isQboConnected ?? false) &&
    unpushedFinalizedCount.value > 0,
);

const confirmDisabledReason = computed(() => {
  if (uiState.loadError) return 'Unable to load lien data. Please retry.';
  if (liensData.value?.hasUnfinalizedLiens) return 'All liens must be Finalized or submitted to QuickBooks before confirming.';
  return undefined;
});

// ── Lifecycle ────────────────────────────────────────────
onMounted(async () => {
  await loadLiensFromTable();
});

// ── Actions ──────────────────────────────────────────────
async function loadLiensFromTable() {
  uiState.loading = true;
  uiState.loadError = false;
  try {
    await settlementStore.loadLiensFromTable();
  } catch {
    uiState.loadError = true;
  } finally {
    uiState.loading = false;
  }
}

async function updateLienStatus(lien: FromTableLien, newStatus: string) {
  try {
    await lienStore.updateLienStatus(lien.lienId, newStatus);
    handleSuccess('Lien status updated');
    await loadLiensFromTable();
  } catch (e) {
    handleError(e);
  }
}

async function confirmLiens() {
  uiState.saving = true;
  try {
    await settlementStore.confirmLiensFF();
    emit('confirmed');
  } catch (e: any) {
    if (e.response?.data?.error === 'LIENS_NOT_FINALIZED') {
      handleError('All liens must be Finalized or submitted to QuickBooks before confirming.');
      await loadLiensFromTable();
    }
  } finally {
    uiState.saving = false;
  }
}

async function openEditPanel(lien: FromTableLien) {
  try {
    // If panel is already open, close it first so the Transition animates out/in
    if (showEditPanel.value) {
      showEditPanel.value = false;
      await new Promise((r) => setTimeout(r, 10));
    }
    // Open panel immediately with skeleton, then fetch full lien data
    editingLien.value = null;
    panelLoading.value = true;
    showEditPanel.value = true;
    editingLien.value = await lienStore.fetchLienById(lien.lienId);
  } catch (e) {
    showEditPanel.value = false;
    handleError(e);
  } finally {
    panelLoading.value = false;
  }
}

async function onLienSaved() {
  // Panel closes itself; just refresh data so totals/statuses update
  editingLien.value = null;
  await loadLiensFromTable();
}

function goToLiensTab() {
  props.state?.go('matters-next.client.matter.liens');
}

</script>

<style scoped>
.lien-status-badge { font-size: 12px; letter-spacing: 0; }
.lien-status-caret { opacity: 0.5; margin-left: 2px; }
.lien-status--pending.v-chip    { background-color: #f1f5f9 !important; color: #475569 !important; border: 1px solid #e2e8f0 !important; }
.lien-status--negotiating.v-chip { background-color: #dbeafe !important; color: #1e40af !important; border: 1px solid #bfdbfe !important; }
.lien-status--finalized.v-chip  { background-color: #dcfce7 !important; color: #166534 !important; border: 1px solid #bbf7d0 !important; }
.lien-status--pushedtoqbo.v-chip { background-color: #dcfce7 !important; color: #166534 !important; border: 1px solid #bbf7d0 !important; }
</style>
